import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Ticket, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AdminVouchers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [items, setItems] = useState([]);
  const [code, setCode] = useState('');
  const [type, setType] = useState('percent');
  const [value, setValue] = useState(10);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch (_) {
      navigate('/admin/login');
      return;
    }
    load();
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/vouchers');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      toast.error('Gagal memuat voucher');
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    const c = (code || '').trim().toUpperCase();
    if (!c) {
      toast.error('Kode voucher wajib');
      return;
    }
    setCreating(true);
    try {
      await api.post('/admin/vouchers', {
        code: c,
        type,
        value: Number(value),
        is_active: isActive,
      });
      toast.success('Voucher berhasil dibuat');
      setCode('');
      setType('percent');
      setValue(10);
      setIsActive(true);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal membuat voucher');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Link to="/admin/dashboard" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div className="flex items-center gap-3">
                <Ticket className="w-6 h-6 text-primary" />
                <span className="text-xl font-heading font-bold italic text-white uppercase">Voucher</span>
              </div>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="px-4 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-white hover:border-primary/50 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
              <div className="text-white font-bold uppercase mb-4">Buat Voucher (1x pakai)</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="KODE (misal: PROMO50)"
                  className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                />
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                >
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed</option>
                </select>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={type === 'percent' ? 'Nilai (%)' : 'Nilai (Rp)'}
                  className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                />
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsActive((v) => !v)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-800 text-white hover:bg-zinc-700"
                  >
                    {isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    {isActive ? 'Aktif' : 'Nonaktif'}
                  </button>
                  <button
                    onClick={create}
                    disabled={creating}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-black font-bold hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    Simpan
                  </button>
                </div>
              </div>
              <div className="text-xs text-zinc-500 mt-3">Voucher akan dianggap terpakai saat pembayaran di-verify (booking confirmed).</div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-zinc-800 text-white font-bold uppercase">Daftar Voucher</div>
              <div className="divide-y divide-zinc-800">
                {items.length === 0 ? (
                  <div className="p-6 text-zinc-500">Belum ada voucher</div>
                ) : (
                  items.map((v) => (
                    <div key={v.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-white font-bold">{v.code}</div>
                        <div className="text-xs text-zinc-500">
                          {String(v.type).toUpperCase()} • {v.type === 'percent' ? `${v.value}%` : `Rp ${Number(v.value).toLocaleString('id-ID')}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${
                          v.is_active ? 'bg-primary/15 text-primary border-primary/30' : 'bg-zinc-800 text-zinc-300 border-zinc-700'
                        }`}>
                          {v.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${
                          v.used_booking_id ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-secondary/15 text-secondary border-secondary/30'
                        }`}>
                          {v.used_booking_id ? 'USED' : 'UNUSED'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminVouchers;

